import { ReactNode } from 'react';
import { CheckCircle2, Circle, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/components/ui/accordion';
import { Badge } from '@/shared/components/ui/badge';
import { OnboardingTask, OnboardingTaskStatus } from '@/types/api/group/onboarding-task';
import {
  onboardingDescription,
  onboardingDisabledReason,
  onboardingDuration,
  onboardingLabel,
} from '../../utils/onboarding-labels';

type OnboardingTaskCardProps = {
  task: OnboardingTask;
  /** The task's inline form or action; without it the card can't be expanded */
  children?: ReactNode;
  /** Keep the content mounted while collapsed, e.g. so a dialog opened from it stays open */
  keepMounted?: boolean;
};

export function OnboardingTaskCard({ task, children, keepMounted }: OnboardingTaskCardProps) {
  const completed = task.status === OnboardingTaskStatus.Completed;
  const disabled = task.status === OnboardingTaskStatus.Disabled;

  return (
    <AccordionItem
      value={task.key}
      disabled={disabled || !children}
      className={cn(
        'rounded-xl border bg-white dark:bg-gray-800 shadow-sm px-5 last:border-b',
        'data-[state=open]:border-primary/40',
        disabled && 'opacity-60',
      )}
    >
      <AccordionTrigger className="hover:no-underline items-start">
        <div className="flex items-start gap-3">
          <TaskStatusIcon status={task.status} />
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className={cn('text-base', completed && 'text-muted-foreground')}>{onboardingLabel(task.key)}</span>
              {completed && <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">Klart</Badge>}
            </div>
            <p className="text-sm font-normal text-muted-foreground">
              {disabled
                ? onboardingDisabledReason(task.key)
                : `${onboardingDescription(task.key)} · ${onboardingDuration(task.key)}`}
            </p>
          </div>
        </div>
      </AccordionTrigger>
      {/* Top padding so floating input labels aren't cut off by the content's overflow clipping */}
      {children && (
        <AccordionContent
          forceMount={keepMounted || undefined}
          // Radix leaves force-mounted content visible when closed
          className={cn('pl-8 pt-2 pb-5', keepMounted && '[[data-state=closed]>&]:hidden')}
        >
          {children}
        </AccordionContent>
      )}
    </AccordionItem>
  );
}

export function TaskStatusIcon({ status }: { status: OnboardingTaskStatus }) {
  // prettier-ignore
  switch (status) {
    case OnboardingTaskStatus.Completed: return <CheckCircle2 className="size-5 shrink-0 text-emerald-500" aria-label="Klart" />;
    case OnboardingTaskStatus.Disabled:  return <Lock className="size-5 shrink-0 text-gray-400" aria-label="Låst" />;
    default:                             return <Circle className="size-5 shrink-0 text-primary" aria-label="Att göra" />;
  }
}
